import { useCallback, useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { apiClient } from '../../lib/api-client';
import Editor from 'react-simple-code-editor';
import { highlight, languages } from 'prismjs';
import 'prismjs/components/prism-yaml';
import 'prismjs/themes/prism-tomorrow.css';
import yaml from 'js-yaml';
import { Skeleton } from '../ui/Skeleton';
import { TenantConfigForm } from './tenant-config-form/TenantConfigForm';
import { MasterConfigForm } from './master-config-form/MasterConfigForm';
import {
  cloneDoc,
  parseTenantYaml,
  tenantDocToYaml,
  type TenantConfigDoc,
} from '../../lib/tenant-config-form-utils';
import {
  parseMasterYaml,
  masterDocToYaml,
  type MasterConfigDoc,
} from '../../lib/master-config-form-utils';

interface TenantEditDialogProps {
  tenantCode: string | null;
  onClose: () => void;
  onSuccess: () => void;
  isMaster?: boolean;
}

type EditorMode = 'form' | 'yaml';

export function TenantEditDialog({ tenantCode, onClose, onSuccess, isMaster = false }: TenantEditDialogProps) {
  const [mode, setMode] = useState<EditorMode>('form');
  const [yamlContent, setYamlContent] = useState('');
  const [formDoc, setFormDoc] = useState<TenantConfigDoc | MasterConfigDoc>({});
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [masterProducts, setMasterProducts] = useState<Array<{ id: string; workspaceDir?: string }>>([]);

  const loadYaml = useCallback(async () => {
    setLoading(true);
    setError(null);
    setParseError(null);

    try {
      await apiClient.refreshTenantCache();

      let response;
      if (isMaster) {
        response = await apiClient.getMasterYaml();
      } else if (tenantCode) {
        response = await apiClient.getTenantYaml(tenantCode);
      } else {
        return;
      }

      const content = response.yaml || '';
      setYamlContent(content);
      setFormDoc(isMaster ? parseMasterYaml(content) : parseTenantYaml(content));

      if (!isMaster && tenantCode) {
        try {
          const tenantRes = await apiClient.getTenantByCode(tenantCode);
          setLogoUrl(tenantRes?.tenant?.logoUrl ?? null);
        } catch {
          setLogoUrl(null);
        }
        try {
          const { products: masterList } = await apiClient.getCicdProducts();
          setMasterProducts(
            (masterList || []).map((p: { id: string; workspaceDir?: string }) => ({
              id: p.id,
              workspaceDir: p.workspaceDir,
            })),
          );
        } catch {
          setMasterProducts([]);
        }
      } else {
        setLogoUrl(null);
        setMasterProducts([]);
      }
    } catch (err: any) {
      setError(err.message || `Failed to load ${isMaster ? 'master' : 'tenant'} YAML`);
    } finally {
      setLoading(false);
    }
  }, [isMaster, tenantCode]);

  useEffect(() => {
    if (tenantCode || isMaster) {
      setMode('form');
      void loadYaml();
    }
  }, [tenantCode, isMaster, loadYaml]);

  const validateYaml = (content: string): boolean => {
    try {
      yaml.load(content);
      setParseError(null);
      return true;
    } catch (err: any) {
      setParseError(`Invalid YAML: ${err.message}`);
      return false;
    }
  };

  const switchMode = (next: EditorMode) => {
    if (next === mode) return;

    if (next === 'yaml') {
      try {
        setYamlContent(isMaster ? masterDocToYaml(formDoc as MasterConfigDoc) : tenantDocToYaml(formDoc as TenantConfigDoc));
        setParseError(null);
      } catch (e: any) {
        setParseError(e.message);
        return;
      }
    }

    if (mode === 'yaml' && next === 'form') {
      try {
        setFormDoc(isMaster ? parseMasterYaml(yamlContent) : parseTenantYaml(yamlContent));
        setParseError(null);
      } catch (e: any) {
        setParseError(e.message);
        return;
      }
    }

    setMode(next);
  };

  const handleSave = async () => {
    let contentToSave = yamlContent;

    if (mode === 'form') {
      try {
        contentToSave = isMaster
          ? masterDocToYaml(formDoc as MasterConfigDoc)
          : tenantDocToYaml(formDoc as TenantConfigDoc);
        setParseError(null);
      } catch (e: any) {
        setParseError(e.message);
        return;
      }
    }

    if (!validateYaml(contentToSave)) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (isMaster) {
        await apiClient.updateMasterYaml(contentToSave);
      } else if (tenantCode) {
        await apiClient.updateTenantYaml(tenantCode, contentToSave);
      }
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || `Failed to save ${isMaster ? 'master' : 'tenant'} configuration`);
    } finally {
      setSaving(false);
    }
  };

  const handleYamlChange = (newValue: string) => {
    setYamlContent(newValue);
    setValidationErrorFromYaml(newValue);
  };

  const setValidationErrorFromYaml = (content: string) => {
    try {
      yaml.load(content);
      setParseError(null);
    } catch (err: any) {
      setParseError(`Invalid YAML: ${err.message}`);
    }
  };

  const title = isMaster ? 'Edit Master Configuration' : `Edit Tenant: ${tenantCode}`;
  const description = isMaster
    ? 'Edit master configuration — form for common fields, YAML for cache policies and advanced blocks'
    : 'Edit tenant configuration — form for common fields, YAML for advanced nested blocks';

  return (
    <Dialog open={!!(tenantCode || isMaster)} onOpenChange={(open) => !open && onClose()}>
      <DialogContent maxWidth="4xl" className="max-h-[90vh] flex flex-col">
        <DialogHeader>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <DialogTitle>{title}</DialogTitle>
              <DialogDescription>{description}</DialogDescription>
            </div>
            <div className="flex items-center border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden shrink-0">
              <button
                type="button"
                onClick={() => switchMode('form')}
                className={`px-3 py-1.5 text-xs font-medium ${
                  mode === 'form'
                    ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                    : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                Form
              </button>
              <button
                type="button"
                onClick={() => switchMode('yaml')}
                className={`px-3 py-1.5 text-xs font-medium ${
                  mode === 'yaml'
                    ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                    : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                YAML
              </button>
            </div>
          </div>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-3 min-h-0 overflow-y-auto">
          {loading && (
            <div className="space-y-2 py-4">
              <Skeleton className="h-64 w-full" />
            </div>
          )}

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-800 dark:text-red-200">
              {error}
            </div>
          )}

          {parseError && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 text-amber-800 dark:text-amber-200">
              {parseError}
            </div>
          )}

          {!loading && mode === 'form' && isMaster && (
            <MasterConfigForm
              doc={formDoc as MasterConfigDoc}
              onChange={(next) => setFormDoc(cloneDoc(next))}
            />
          )}

          {!loading && !isMaster && mode === 'form' && (
            <TenantConfigForm
              doc={formDoc as TenantConfigDoc}
              onChange={(next) => setFormDoc(cloneDoc(next))}
              tenantCode={tenantCode ?? undefined}
              logoUrl={logoUrl}
              onLogoChange={setLogoUrl}
              masterProducts={masterProducts}
            />
          )}

          {!loading && mode === 'yaml' && (
            <div className="relative">
              <div className="bg-gray-900 rounded-lg overflow-hidden border-2 border-gray-700 focus-within:border-blue-500">
                <div className="max-h-[min(560px,55vh)] overflow-auto custom-scrollbar">
                  <Editor
                    value={yamlContent}
                    onValueChange={handleYamlChange}
                    highlight={(code: string) => highlight(code, languages.yaml, 'yaml')}
                    padding={16}
                    style={{
                      fontFamily: '"Fira Code", "Fira Mono", monospace',
                      fontSize: 13,
                      backgroundColor: '#1e293b',
                      color: '#e2e8f0',
                      minHeight: 280,
                    }}
                    className="outline-none"
                  />
                </div>
              </div>
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button onClick={onClose} disabled={saving} variant="secondary">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || loading || !!parseError}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
